import { IconButton } from "@/atoms";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

import { ONE_MEGABYTE } from "@oneleet/utils";

import { ArrowUpFromLineIcon, File, Image, X } from "lucide-react";
import { twMerge } from "tailwind-merge";

const DEFAULT_MAX_FILE_SIZE = 20 * ONE_MEGABYTE;

interface FileDropzoneProps {
  /**
   * Label will be shown above the dropzone
   * Defaults to "Drag and drop or browse files"
   */
  label?: string;
  /**
   * Description will be shown below the label, use to provide more context on
   * what the dropzone is for
   */
  description?: string;
  /**
   * Class name for the description
   */
  descriptionClassName?: string;

  /**
   * Icon to show in the dropzone
   * Defaults to ArrowUpFromLineIcon
   */
  icon?: React.ReactElement<{ className: string }>;

  multiple?: boolean;
  /**
   * Map of accepted MIME types and their corresponding file extensions
   * Example:
   * {
   *   "image/jpeg": [".jpg", ".jpeg"],
   *   "image/png": [".png"],
   * }
   */
  accept?: Record<string, string[]>;
  /**
   * Max file size in bytes
   */
  maxSize?: number;
  disabled?: boolean;
  className?: string;
  onDrop?: (files: File[], errors?: FileRejection[]) => void;
  onFileAccepted?: (file: File) => void;
  onFileRejected?: (file: File) => void;
}

export type FileDropzoneRef = {
  openFilePicker: () => void;
};

const FileDropzone = forwardRef<FileDropzoneRef, FileDropzoneProps>(
  (
    {
      label = "Drag and drop or browse files",
      description,
      icon,
      multiple = false,
      accept,
      maxSize = DEFAULT_MAX_FILE_SIZE,
      disabled = false,
      className,
      onDrop,
      onFileAccepted,
      onFileRejected,
    },
    ref,
  ) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      accept: accept,
      maxSize,
      disabled,
      multiple,
      onDrop: (acceptedFiles, fileRejections) => {
        const acceptedAndRejectedFiles = multiple
          ? [
              ...acceptedFiles,
              ...fileRejections.map((rejection) => rejection.file),
            ]
          : acceptedFiles.length > 0
            ? [acceptedFiles[0]]
            : fileRejections.length > 0
              ? [fileRejections[0].file]
              : [];

        setSelectedFiles(acceptedAndRejectedFiles);

        onDrop?.(acceptedAndRejectedFiles, fileRejections);
        if (acceptedFiles.length > 0) {
          acceptedFiles.forEach((file) => onFileAccepted?.(file));
        } else if (fileRejections.length > 0) {
          fileRejections.forEach((rejection) =>
            onFileRejected?.(rejection.file),
          );
        }
      },
    });

    // Add setSelectedFiles to the ref
    useImperativeHandle(
      ref,
      () => ({
        openFilePicker: open,
      }),
      [open],
    );

    const handleRemoveFile = (fileToRemove: File) => {
      setSelectedFiles((prevFiles) =>
        prevFiles.filter((file) => file !== fileToRemove),
      );
    };

    const getIcon = (file: File) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

      return imageExtensions.includes(extension ?? "") ? (
        <Image className="size-6 text-gray-700" />
      ) : (
        <File className="size-6 text-gray-700" />
      );
    };

    return (
      <div className={className}>
        {selectedFiles.length === 0 ? (
          <div
            {...getRootProps()}
            className={twMerge(
              "flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6",
              isDragActive
                ? "border-brand-500 bg-brand-50"
                : selectedFiles.length > 0
                  ? "border-gray-300 bg-gray-100"
                  : "border-gray-300",
              disabled
                ? "cursor-not-allowed opacity-50"
                : "hover:border-brand-500 hover:bg-brand-50",
            )}
          >
            <input {...getInputProps()} />
            {icon && React.isValidElement(icon) ? (
              React.cloneElement(icon, {
                className: twMerge(
                  "text-brand-500 size-9",
                  icon.props.className,
                ),
              })
            ) : (
              <ArrowUpFromLineIcon className="text-brand-500 size-9" />
            )}
            <p className="mt-4">
              {label ? (
                label
              ) : (
                <>
                  Drag and drop or{" "}
                  <span className="text-brand-500 font-semibold">
                    browse files
                  </span>
                </>
              )}
            </p>
            {description && (
              <p className={twMerge("text-xs leading-5 text-gray-600")}>
                {description}
              </p>
            )}
          </div>
        ) : (
          selectedFiles.map((file) => (
            <div
              className="mt-2 rounded-md border px-4 py-2 shadow-sm"
              key={file.name}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-row items-center gap-3">
                  {getIcon(file)}
                  <span className="line-clamp-1">{file.name}</span>
                </div>
                <div className="flex -flex-col gap-1">
                  <IconButton
                    icon={<X />}
                    onClick={() => {
                      handleRemoveFile(file);
                    }}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  },
);

FileDropzone.displayName = "FileDropzone";

export default FileDropzone;
